import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from './store/store';
import Layout from './components/Layout/Layout';
import LoginPage from './pages/Auth/LoginPage';
import DashboardPage from './pages/Dashboard/DashboardPage';
import AppointmentsPage from './pages/Appointments/AppointmentsPage';
import DoctorsPage from './pages/Doctors/DoctorsPage';
import PatientsPage from './pages/Patients/PatientsPage';
import BillingPage from './pages/Billing/BillingPage';
import UsersPage from './pages/Users/UsersPage';
import SettingsPage from './pages/Settings/SettingsPage';
import LicenseStatusPage from './pages/License/LicenseStatusPage';
import DoctorSchedulePage from './pages/Doctors/DoctorSchedulePage';
// Medical Management Pages
import VisitsPage from './pages/Medical/VisitsPage';
import MedicationsPage from './pages/Medical/MedicationsPage';
import LabOrdersPage from './pages/Medical/LabOrdersPage';
import CarePlansPage from './pages/CarePlans/CarePlansPage';
import ClinicalNotesPage from './pages/ClinicalNotes/ClinicalNotesPage';
import AllergiesPage from './pages/Allergies/AllergiesPage';
import ImmunizationsPage from './pages/Immunizations/ImmunizationsPage';
import ProtectedRoute from './components/ProtectedRoute';

// Component to handle route protection
const RequireAuth = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

function App() {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const location = useLocation();

  // If on login page and already authenticated, redirect to dashboard
  if (location.pathname === '/login' && isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LoginPage />} />
      
      {/* Protected Routes - Only render Layout and protected content when authenticated */}
      <Route element={
        <RequireAuth>
          <Layout />
        </RequireAuth>
      }>
        {/* Redirect root to dashboard */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        
        {/* Dashboard accessible to all authenticated users */}
        <Route path="/dashboard" element={<ProtectedRoute element={<DashboardPage />} />} />
        
        {/* Role-specific routes */}
        <Route path="/appointments" element={<ProtectedRoute element={<AppointmentsPage />} />} />
        <Route path="/doctors" element={<ProtectedRoute element={<DoctorsPage />} roles={['ADMIN', 'RECEPTIONIST']} />} />
        <Route path="/doctors/schedule" element={<ProtectedRoute element={<DoctorSchedulePage />} roles={['DOCTOR']} />} />
        <Route path="/patients" element={<ProtectedRoute element={<PatientsPage />} roles={['ADMIN', 'DOCTOR', 'NURSE', 'RECEPTIONIST']} />} />
        
        {/* Medical Management Routes */}
        <Route path="/visits" element={<ProtectedRoute element={<VisitsPage />} roles={['ADMIN', 'DOCTOR', 'NURSE']} />} />
        <Route path="/medications" element={<ProtectedRoute element={<MedicationsPage />} roles={['ADMIN', 'DOCTOR', 'NURSE', 'PHARMACIST']} />} />
        <Route path="/lab-orders" element={<ProtectedRoute element={<LabOrdersPage />} roles={['ADMIN', 'DOCTOR', 'NURSE', 'LAB_TECHNICIAN']} />} />
        <Route path="/care-plans" element={<ProtectedRoute element={<CarePlansPage />} roles={['ADMIN', 'DOCTOR', 'NURSE']} />} />
        <Route path="/clinical-notes" element={<ProtectedRoute element={<ClinicalNotesPage />} roles={['ADMIN', 'DOCTOR', 'NURSE']} />} />
        <Route path="/allergies" element={<ProtectedRoute element={<AllergiesPage />} roles={['ADMIN', 'DOCTOR', 'NURSE']} />} />
        <Route path="/immunizations" element={<ProtectedRoute element={<ImmunizationsPage />} roles={['ADMIN', 'DOCTOR', 'NURSE']} />} />
        
        <Route path="/billing" element={<ProtectedRoute element={<BillingPage />} roles={['ADMIN', 'BILLING_STAFF']} />} />
        <Route path="/users" element={<ProtectedRoute element={<UsersPage />} roles={['ADMIN']} />} />
        <Route path="/settings" element={<ProtectedRoute element={<SettingsPage />} roles={['ADMIN', 'DOCTOR']} />} />
        <Route path="/license" element={<ProtectedRoute element={<LicenseStatusPage />} roles={['ADMIN']} />} />
        
        {/* Catch all route - redirect to dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}

export default App; 